import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Checkbox,
  Divider,
  Typography,
  Box,
  Alert,
  Chip,
  Tabs,
  Tab,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Add as AddIcon, Group as GroupIcon, Person as PersonIcon } from '@mui/icons-material';
import apiAxiosInstance from '../../../utils/axiosInstance';
import { useTeacher } from '../../../context/TeacherContext';

interface Student {
  _id: string;
  firstName: string;
  lastName: string;
  gradeLevel?: string;
}

interface AddGroupDialogProps {
  open: boolean;
  onClose: () => void;
  onGroupAdded: (group: any) => void;
}

const GRADE_LEVELS = ['K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

const AddGroupDialog: React.FC<AddGroupDialogProps> = ({
  open,
  onClose,
  onGroupAdded,
}) => {
  const { teacher } = useTeacher();
  const [groupName, setGroupName] = useState('');
  const [groupType, setGroupType] = useState('behavior');
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  // New student form state
  const [newStudentFirstName, setNewStudentFirstName] = useState('');
  const [newStudentLastName, setNewStudentLastName] = useState('');
  const [newStudentGrade, setNewStudentGrade] = useState('');
  const [addingStudent, setAddingStudent] = useState(false);

  useEffect(() => {
    if (open) {
      loadStudents();
    }
  }, [open]);

  const loadStudents = async () => {
    try {
      const response = await apiAxiosInstance.get('/api/students');
      setStudents(response.data || []);
    } catch (err) {
      console.error('Failed to load students', err);
    }
  };

  const handleClose = () => {
    setGroupName('');
    setGroupType('behavior');
    setSelectedStudentIds([]);
    setError(null);
    setActiveTab(0);
    resetNewStudentForm();
    onClose();
  };

  const resetNewStudentForm = () => {
    setNewStudentFirstName('');
    setNewStudentLastName('');
    setNewStudentGrade('');
    setAddingStudent(false);
  };

  const toggleStudent = (studentId: string) => {
    const id = String(studentId);
    setSelectedStudentIds((prev) =>
      prev.includes(id)
        ? prev.filter((sid) => sid !== id)
        : [...prev, id]
    );
  };

  const handleAddNewStudent = async () => {
    if (!newStudentFirstName.trim() || !newStudentLastName.trim()) {
      setError('First name and last name are required');
      return;
    }

    if (!newStudentGrade) {
      setError('Grade level is required');
      return;
    }

    if (!teacher?._id) {
      setError('You must be logged in to add a student');
      return;
    }

    setAddingStudent(true);
    setError(null);

    try {
      const response = await apiAxiosInstance.post('/api/students', {
        teacherId: teacher._id,
        firstName: newStudentFirstName.trim(),
        lastName: newStudentLastName.trim(),
        gradeLevel: newStudentGrade,
        hasBehaviorCases: false,
      });

      // API returns { message: "...", student: {...} }
      const newStudent = response.data.student || response.data;
      console.log('[DEBUG] New student response:', response.data);
      console.log('[DEBUG] Extracted student:', newStudent);
      console.log('[DEBUG] First name:', newStudent.firstName, 'Last name:', newStudent.lastName);
      
      // Ensure the student object has the correct structure
      const normalizedStudent: Student = {
        _id: String(newStudent._id),
        firstName: newStudent.firstName || '',
        lastName: newStudent.lastName || '',
        gradeLevel: newStudent.gradeLevel,
      };
      
      setStudents((prev) => [...prev, normalizedStudent]);
      setSelectedStudentIds((prev) => [...prev, normalizedStudent._id]);
      resetNewStudentForm();
      setActiveTab(0); // Switch back to select tab
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to add student');
    } finally {
      setAddingStudent(false);
    }
  };

  const handleSubmit = async () => {
    if (!groupName.trim()) {
      setError('Group name is required');
      return;
    }

    if (selectedStudentIds.length === 0) {
      setError('Please select at least one student');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Ensure all student IDs are strings
      const studentIds = selectedStudentIds.map(id => String(id));
      console.log('Creating group with:', { name: groupName.trim(), type: groupType, students: studentIds });
      
      const response = await apiAxiosInstance.post('/api/groups', {
        name: groupName.trim(),
        type: groupType,
        students: studentIds,
      });

      onGroupAdded(response.data);
      handleClose();
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to add group');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GroupIcon />
          Create New Group
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          label="Group Name"
          value={groupName}
          onChange={(e) => setGroupName(e.target.value)}
          autoFocus
          required
          sx={{ mb: 2 }}
        />

        <input type="hidden" value={groupType} />

        <Divider sx={{ mb: 2 }} />

        <Typography variant="subtitle1" gutterBottom>
          Students
          <Chip
            label={`${selectedStudentIds.length} selected`}
            size="small"
            color="primary"
            sx={{ ml: 1 }}
          />
        </Typography>

        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{ mb: 1 }}
        >
          <Tab label="Select Existing" />
          <Tab label="Add New Student" />
        </Tabs>

        {activeTab === 0 ? (
          <Box sx={{ maxHeight: 250, overflow: 'auto' }}>
            {students.length === 0 ? (
              <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                No students available. Click "Add New Student" tab to add one.
              </Typography>
            ) : (
              <List dense>
                {students.map((student) => (
                  <ListItem
                    key={student._id}
                    button
                    onClick={() => toggleStudent(student._id)}
                    selected={selectedStudentIds.includes(String(student._id))}
                  >
                    <Checkbox
                      checked={selectedStudentIds.includes(String(student._id))}
                      tabIndex={-1}
                      disableRipple
                    />
                    <ListItemAvatar>
                      <Avatar sx={{ width: 32, height: 32 }}>
                        {(student.firstName?.[0] || '') + (student.lastName?.[0] || '')}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={`${student.firstName || 'Unknown'} ${student.lastName || ''}`}
                      secondary={student.gradeLevel ? `Grade ${student.gradeLevel}` : 'No grade'}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        ) : (
          <Box sx={{ py: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name *"
                  value={newStudentFirstName}
                  onChange={(e) => setNewStudentFirstName(e.target.value)}
                  autoFocus
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name *"
                  value={newStudentLastName}
                  onChange={(e) => setNewStudentLastName(e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Grade Level *</InputLabel>
                  <Select
                    value={newStudentGrade}
                    label="Grade Level"
                    onChange={(e) => setNewStudentGrade(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Select Grade</em>
                    </MenuItem>
                    {GRADE_LEVELS.map((grade) => (
                      <MenuItem key={grade} value={grade}>
                        Grade {grade}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  onClick={handleAddNewStudent}
                  disabled={addingStudent}
                  startIcon={<PersonIcon />}
                  fullWidth
                >
                  {addingStudent ? 'Adding...' : 'Add Student & Select'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !groupName.trim() || selectedStudentIds.length === 0}
          startIcon={<AddIcon />}
        >
          {loading ? 'Creating...' : 'Create Group'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddGroupDialog;
